import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PAGE_ZOOM_CHANGED_EVENT } from '../../utils/pageZoom';
import { NavCameraIcon } from './AiPhotoIcons';

type Props = {
  /** 찍은 사진(원본 크기 JPEG) — 축소는 호출 쪽 resizeImageFile에서 */
  onCapture: (file: File) => void;
  onClose: () => void;
  /** getUserMedia를 쓸 수 없을 때(권한 거부·미지원) 기본 카메라 입력으로 대체 */
  onFallback: () => void;
};

/** 실시간 미리보기 촬영 화면 — 셔터 · 취소 · 카메라 전환, 닫을 때 스트림 정지 */
const AiCameraCapture: React.FC<Props> = ({ onCapture, onClose, onFallback }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const [ready, setReady] = useState(false);
  const [canSwitch, setCanSwitch] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let alive = true;
    setReady(false);
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    if (!md?.getUserMedia) {
      onFallback();
      return;
    }
    md.getUserMedia({ video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false })
      .then(async (stream) => {
        if (!alive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        stop();
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          await v.play().catch(() => undefined);
        }
        setReady(true);
        try {
          const devs = await md.enumerateDevices();
          if (alive) setCanSwitch(devs.filter((d) => d.kind === 'videoinput').length > 1);
        } catch {
          /* 장치 목록을 못 읽으면 전환 버튼만 숨김 */
        }
      })
      .catch(() => {
        if (alive) onFallback();
      });
    return () => {
      alive = false;
    };
  }, [facing, onFallback, stop]);

  useEffect(() => () => stop(), [stop]);

  /** 저장되는 프레임 비율로 모달을 맞춰, 잘림 없이 미리보기 전체가 보이게 한다 */
  const fitModal = useCallback(() => {
    const v = videoRef.current;
    const modal = rootRef.current?.closest('.cp-aip-modal') as HTMLElement | null;
    if (!v || !modal || !v.videoWidth || !v.videoHeight) return;
    const bar = barRef.current?.offsetHeight ?? 62;
    modal.style.setProperty('--aip-cam-ratio', String(v.videoWidth / v.videoHeight));
    modal.style.setProperty('--aip-cam-bar', `${bar}px`);
    modal.classList.add('is-cam-fit');
  }, []);

  useEffect(() => {
    if (ready) fitModal();
  }, [ready, facing, fitModal]);

  useEffect(() => {
    const onResize = () => fitModal();
    window.addEventListener('resize', onResize);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, onResize);
      const modal = rootRef.current?.closest('.cp-aip-modal') as HTMLElement | null;
      modal?.classList.remove('is-cam-fit');
      modal?.style.removeProperty('--aip-cam-ratio');
      modal?.style.removeProperty('--aip-cam-bar');
    };
  }, [fitModal]);

  const close = () => {
    stop();
    onClose();
  };

  const shoot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')?.drawImage(v, 0, 0);
    c.toBlob(
      (blob) => {
        if (!blob) return;
        stop();
        onCapture(new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.92
    );
  };

  return (
    <div ref={rootRef} className="cp-aip-cam" role="dialog" aria-modal="true" aria-label="사진 찍기" onClick={(e) => e.stopPropagation()}>
      <div className="cp-aip-cam-view">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className={facing === 'user' ? 'is-mirror' : ''}
          onLoadedMetadata={fitModal}
        />
        {!ready ? <p className="cp-aip-cam-wait">카메라를 여는 중…</p> : null}
      </div>
      <div ref={barRef} className="cp-aip-cam-bar">
        <button type="button" className="cp-aip-cam-cancel" onClick={close}>
          취소
        </button>
        <button type="button" className="cp-aip-cam-shutter" onClick={shoot} disabled={!ready} aria-label="촬영">
          <span />
        </button>
        <button
          type="button"
          className="cp-aip-cam-switch"
          onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}
          disabled={!canSwitch}
          aria-label="카메라 전환"
          title="카메라 전환"
        >
          <NavCameraIcon />
        </button>
      </div>
    </div>
  );
};

export default AiCameraCapture;
